/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, TextField, Autocomplete } from '@mui/material';
import { useStyles } from '../../shared/PolicyDialog/styles';
import Functional from './Functional/Functional';
import Data from './Data/Data';
import Entities from '../../shared/PolicyDialog/Entities/Entities';
import Dimension from './Dimension/Dimension';
import DimensionV2 from './DimensionV2/DimensionV2';
import PolicyDialog from '../../shared/PolicyDialog/PolicyDialog';
import { IAddPolicyDialogProps, IOptionPolicyType } from '../../models/Roles/RolesContracts';
import {
  POLICY_CREATE_SUCCESS,
  POLICY_CREATE_ERROR,
  ADD_POLICY_DIALOG_TITLE,
  SAVE_BTN_TEXT,
  DISPLAY_NAME,
  POLICY_TYPE,
  FUNCTIONAL,
  DATA,
  ENTITIES,
  EDIT_POLICY_DIALOG_TITLE,
  POLICY_EDIT_ERROR,
  POLICY_EDIT_SUCCESS,
  UPDATE_POLICY_BTN_TEXT,
  DIMENSION,
  MEASURE,
  POLICY_TYPE_LIST,
  DIMENSIONV2,
  NO_RECORDS_IN_SEARCH,
  POLICY_TYPE_WITHOUT_DIMENSION_V2_LIST
} from '../../shared/PolicyDialog/constants';
import { createNewPolicy, editPolicy } from '../../services/policy';
import { IPolicyRuleData } from '../../models/Policies/PolicyContract';
import interpolateString from '../../utility/stringInterpolation';
import { selectFormValidationValues, selectPolicyDataFormValues } from '../../store/role/PolicyData/dataSelector';
import { useDispatch, useSelector } from 'react-redux';
import { resetPolicyFormData } from '../../store/role/PolicyData/dataActionCreator';
import { resetDimensionPolicyData } from '../../store/role/policyDimension/dimensionActionCreator';
import { selectDimensionPolicyData } from '../../store/role/policyDimension/dimensionSelector';
import { compareByProperty, isPortalV2 } from '../../utility/commonUtility';
import Measure from './Measure/Measure';
import { measureInitialState } from '../../store/role/PolicyMeasure/measureReducer';
import { selectMeasuresFormData } from '../../store/role/PolicyMeasure/measureSelector';
import { resetPolicyDialog } from '../../store/role/policy';
import {
  getDataPolicyExpression,
  getDimensionPolicyExpression,
  getMeasurePolicyExpression,
  validateDataPolicy,
  validateMeasuresForm,
  getDimensionPolicyExpressionV2
} from '../../shared/PolicyDialog/PolicyUtils';
import { HARNESS_FEATURE_FLAG } from '../../utility/constants';
import { hideSnackbar, setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../RoleDetails/constants';
import useEntityPolicyForm from '../../shared/PolicyDialog/Entities/useEntityPolicyForm';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';

const AddPolicyDialog = ({
  onSave,
  onClose,
  role,
  refreshPolicyOnSave,
  edit,
  rowData,
}: IAddPolicyDialogProps) => {
  const policyData = useSelector(selectPolicyDataFormValues);
  const dimensionPolicyV2Flag = useFeatureFlag(HARNESS_FEATURE_FLAG.dimentionV2);
  const isDimentionV2PolicyEnabled = isPortalV2() ? true : dimensionPolicyV2Flag;
  const types = isDimentionV2PolicyEnabled ? POLICY_TYPE_LIST : POLICY_TYPE_WITHOUT_DIMENSION_V2_LIST;

  const getDefaultType = useCallback(() => {
    if (rowData) {
      return types.find(type => (type.key === rowData?.policyType.toLowerCase()))
    } else {
      return types.find(type => type.default);
    }
  }, [rowData, types]);

  const dispatch = useDispatch();
  const { errorFormValues: dataFormErrorState } = useSelector(selectFormValidationValues);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [type, setType] = useState<any>(getDefaultType());
  const [permissionId, setPermissionId] = useState<number>(0);
  const measuresFormData = useSelector(selectMeasuresFormData);
  const dimensionPolicyData = useSelector(selectDimensionPolicyData);
  const { classes } = useStyles();

  useEffect(() => {
    setType(getDefaultType());
  }, [getDefaultType]);

  const { isEntitySaveEnabled, submitEntityForm } = useEntityPolicyForm();

  useEffect(() => {
    if (type.key === ENTITIES) {
      setIsSaveDisabled(!isEntitySaveEnabled);
    }
  }, [isEntitySaveEnabled, type?.key]);

  const handleClose = () => {
    type.key === DATA && dispatch(resetPolicyFormData());
    type.key === DIMENSION && dispatch(resetDimensionPolicyData());
    type.key === DIMENSIONV2 && dispatch(resetDimensionPolicyData());
    resetPolicyDialog(dispatch);
    onClose();
  };

  const handleDropdownChange = (_event: React.ChangeEvent<{}>, newValue: IOptionPolicyType | null) => setType(newValue ?? type);

  const handleDropdownFiltering = (options: IOptionPolicyType[], { inputValue }: { inputValue: string }) => {
    return options.filter(
      (option: any) =>
        option.displayName.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.description.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const handleRenderingDropdownOption = (props: any, option: any) => {
    return (
      <Box {...props} className={classes.optionItem}>
        <Box className={classes.optionItemName}>{option.displayName}</Box>
        <Box className={classes.optionItemDescription}>{option.description}</Box>
      </Box>
    )
  };

  const getPermissionId = (id: number) => {
    setIsSaveDisabled(false);
    setPermissionId(id);
  };

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        message: message,
        severity: severity
      })
    );
  };
  const snackbarSetHidden = () => {
    dispatch(hideSnackbar());
  };

  const createAndAssignPolicyToRole = (newPolicyData: IPolicyRuleData) => {
    const request = edit ? editPolicy(role.id, rowData.id, newPolicyData) : createNewPolicy(role.id, newPolicyData);

    return request
      .then(response => {
        handleClose();
        displaySnackbar(
          edit
            ? interpolateString([response.displayName || newPolicyData.displayName, role.name], POLICY_EDIT_SUCCESS)
            : interpolateString([response.displayName || newPolicyData.displayName, role.name], POLICY_CREATE_SUCCESS),
          'success'
        );
        refreshPolicyOnSave();
      })
      .catch((errorMessage: string) => {
        handleClose();
        displaySnackbar((edit ? POLICY_EDIT_ERROR : POLICY_CREATE_ERROR) + errorMessage, 'error');
      });
  };

  const handleSubmit = () => {
    switch (type.key) {
      case DATA:
        if (validateDataPolicy(policyData, dispatch, dataFormErrorState)) {
          snackbarSetHidden();
          setIsSaveDisabled(false);
          const policyExpression = getDataPolicyExpression(policyData);
          const dataPolicy = {
            displayName: policyData.name,
            description: policyData.description,
            expression: policyExpression
          };
          createAndAssignPolicyToRole(dataPolicy);
        }
        break;
      case ENTITIES:
        submitEntityForm(createAndAssignPolicyToRole);
        break;
      case MEASURE:
        if (validateMeasuresForm(measuresFormData, dispatch, measureInitialState.measureFormError)) {
          snackbarSetHidden();
          setIsSaveDisabled(false);
          const policyExpression = getMeasurePolicyExpression(measuresFormData);
          const measurePolicy = {
            displayName: measuresFormData.policyName,
            description: measuresFormData.policyDescription,
            expression: policyExpression
          };
          createAndAssignPolicyToRole(measurePolicy);
        }
        break;
      case DIMENSION:
        snackbarSetHidden();
        setIsSaveDisabled(false);
        const policyExpression = getDimensionPolicyExpression(dimensionPolicyData);
        const dimensionPolicy = {
          displayName: dimensionPolicyData.policyName,
          description: dimensionPolicyData.policyDescription,
          expression: policyExpression
        };
        createAndAssignPolicyToRole(dimensionPolicy);
        break;
      case DIMENSIONV2:
        snackbarSetHidden();
        setIsSaveDisabled(false);
        const policyExpressionV2 = getDimensionPolicyExpressionV2(dimensionPolicyData);
        const dimensionPolicyV2 = {
          displayName: dimensionPolicyData.policyName,
          description: dimensionPolicyData.policyDescription,
          expression: policyExpressionV2
        };
        createAndAssignPolicyToRole(dimensionPolicyV2);
        break;
      case FUNCTIONAL:
        if (permissionId) {
          onSave(permissionId);
          onClose();
        }
        break;
      default:
        setIsSaveDisabled(true);
        break;
    }
  };

  return (
    <PolicyDialog
      type={type}
      handleClose={handleClose}
      dialogTitle={edit ? EDIT_POLICY_DIALOG_TITLE : ADD_POLICY_DIALOG_TITLE}
      saveLabel={edit ? UPDATE_POLICY_BTN_TEXT : SAVE_BTN_TEXT}
      isSaveDisabled={isSaveDisabled}
      handleSubmit={handleSubmit}
    >
      <Grid item xs={12} className={classes.formStyle}>
        <Autocomplete
          id='policy-rule-dropdown'
          value={type}
          options={types.sort(compareByProperty(DISPLAY_NAME))}
          getOptionLabel={(option: any) => option.displayName}
          filterOptions={handleDropdownFiltering}
          renderOption={handleRenderingDropdownOption}
          onChange={handleDropdownChange}
          classes={{ option: classes.optionItem }}
          noOptionsText={NO_RECORDS_IN_SEARCH}
          disableClearable
          disabled={edit}
          renderInput={(params: any) => <TextField {...params} label={POLICY_TYPE} variant="standard" />}
        />
      </Grid>
      {type.key === FUNCTIONAL && (<Functional getId={getPermissionId} />)}
      {type.key === DATA && (<Data {...{ edit, rowData }} setIsSaveDisabled={setIsSaveDisabled} />)}
      {type.key === ENTITIES && (<Entities {...{ edit, rowData }} />)}
      {type.key === DIMENSION && (<Dimension setIsSaveDisabled={setIsSaveDisabled} />)}
      {type.key === DIMENSIONV2 && (<DimensionV2 {...{ edit, rowData }} setIsSaveDisabled={setIsSaveDisabled} />)}
      {type.key === MEASURE && (<Measure setIsSaveDisabled={setIsSaveDisabled} />)}
    </PolicyDialog>
  );
};

export default AddPolicyDialog;
